/**
 * Segment Separation
 * 
 * Creates vertical spacing between nodes when their incoming edges'
 * horizontal segments would overlap.
 *
 * IMPORTANT: We skip edge pairs that share the same parent or the same child,
 * because their horizontal segments stay aligned in Y and pushing does not help.
 */

import { getNodeDimensions } from './constants';

// How much to push a node down when overlap detected
const PUSH_AMOUNT = 40;

// How close (in Y) horizontal segments can be before we consider them overlapping
const OVERLAP_THRESHOLD = 15;

/**
 * Separate overlapping horizontal segments by adjusting node Y positions.
 */
export function separateOverlappingHorizontalSegments(layers, positions, graph, nodeDimensions) {
  if (layers.length < 2) return;

  const getDescendants = (taskId) => {
    const descendants = new Set();
    const queue = [taskId];
    while (queue.length > 0) {
      const current = queue.shift();
      const children = graph.children.get(current) || [];
      for (const child of children) {
        if (!descendants.has(child)) {
          descendants.add(child);
          queue.push(child);
        }
      }
    }
    return descendants;
  };

  const pushDown = (taskId, amount) => {
    const nodesToMove = getDescendants(taskId);
    nodesToMove.add(taskId);
    nodesToMove.forEach(nodeId => {
      if (positions[nodeId]) {
        positions[nodeId] = {
          ...positions[nodeId],
          y: positions[nodeId].y + amount,
        };
      }
    });
  };

  const getHorizontalY = (parentId, childId) => {
    const parentPos = positions[parentId];
    const childPos = positions[childId];
    if (!parentPos || !childPos) return 0;
    const parentHeight = getNodeDimensions(parentId, nodeDimensions).height;
    const parentBottomY = parentPos.y + parentHeight;
    const childY = childPos.y;
    return parentBottomY + (childY - parentBottomY) / 2;
  };

  const getXRange = (parentId, childId) => {
    const parentPos = positions[parentId];
    const childPos = positions[childId];
    if (!parentPos || !childPos) return { minX: 0, maxX: 0 };
    const parentWidth = getNodeDimensions(parentId, nodeDimensions).width;
    const childWidth = getNodeDimensions(childId, nodeDimensions).width;
    const parentX = parentPos.x + parentWidth / 2;
    const childX = childPos.x + childWidth / 2;
    return { minX: Math.min(parentX, childX), maxX: Math.max(parentX, childX) };
  };

  const xRangesOverlap = (r1, r2) => {
    const overlapStart = Math.max(r1.minX, r2.minX);
    const overlapEnd = Math.min(r1.maxX, r2.maxX);
    const overlapAmount = overlapEnd - overlapStart;
    return overlapAmount > 10;
  };

  // Pre-compute descendant sets so we can detect unsolvable conflicts
  const descendantCache = new Map();
  const getDescendantsCached = (taskId) => {
    if (!descendantCache.has(taskId)) {
      descendantCache.set(taskId, getDescendants(taskId));
    }
    return descendantCache.get(taskId);
  };

  const allEdges = [];
  layers.forEach((layer, layerIdx) => {
    if (layerIdx === 0) return;
    layer.forEach(taskId => {
      const parents = graph.parents.get(taskId) || [];
      parents.forEach(parentId => {
        if (positions[parentId] && positions[taskId]) {
          allEdges.push({ parentId, childId: taskId });
        }
      });
    });
  });

  allEdges.sort((a, b) => {
    const cmp = (a.parentId || '').localeCompare(b.parentId || '');
    if (cmp !== 0) return cmp;
    return (a.childId || '').localeCompare(b.childId || '');
  });

  if (allEdges.length < 2) return;

  // Track conflict pairs that have already been attempted, to avoid
  // infinite loops where pushing a child never resolves the overlap
  // (e.g. when the other edge's child is a descendant of the pushed node).
  const attemptedPairs = new Set();

  let iterations = 0;
  const maxIterations = allEdges.length * 3;
  
  while (iterations < maxIterations) {
    iterations++;

    const conflicts = [];
    
    for (let i = 0; i < allEdges.length; i++) {
      for (let j = i + 1; j < allEdges.length; j++) {
        const edgeA = allEdges[i];
        const edgeB = allEdges[j];
        
        if (edgeA.parentId === edgeB.parentId) continue;
        if (edgeA.childId === edgeB.childId) continue;
        
        const pairKey = edgeA.childId < edgeB.childId
          ? `${edgeA.childId}|${edgeB.childId}`
          : `${edgeB.childId}|${edgeA.childId}`;
        if (attemptedPairs.has(pairKey)) continue;
        
        const hYA = getHorizontalY(edgeA.parentId, edgeA.childId);
        const hYB = getHorizontalY(edgeB.parentId, edgeB.childId);
        const rangeA = getXRange(edgeA.parentId, edgeA.childId);
        const rangeB = getXRange(edgeB.parentId, edgeB.childId);
        
        const yDiff = Math.abs(hYA - hYB);
        const hasXOverlap = xRangesOverlap(rangeA, rangeB);
        
        if (yDiff < OVERLAP_THRESHOLD && hasXOverlap) {
          conflicts.push({ i, j, edgeA, edgeB, hYA, hYB, pairKey });
        }
      }
    }
    
    if (conflicts.length === 0) break;
    
    const conflict = conflicts[0];
    let nodeToPush;
    if (Math.abs(conflict.hYA - conflict.hYB) < 0.001) {
      nodeToPush = (conflict.edgeA.childId || '').localeCompare(conflict.edgeB.childId || '') > 0 
        ? conflict.edgeA.childId 
        : conflict.edgeB.childId;
    } else {
      nodeToPush = conflict.hYA > conflict.hYB ? conflict.edgeA.childId : conflict.edgeB.childId;
    }

    // Check if pushing this node would also move the other edge's child
    // (i.e. the other child is a descendant). If so, the push can never
    // resolve the conflict — mark it as attempted and skip.
    const otherChild = nodeToPush === conflict.edgeA.childId
      ? conflict.edgeB.childId
      : conflict.edgeA.childId;
    const descendants = getDescendantsCached(nodeToPush);
    if (descendants.has(otherChild)) {
      attemptedPairs.add(conflict.pairKey);
      continue;
    }
    
    pushDown(nodeToPush, PUSH_AMOUNT);
    // Invalidate descendant cache since positions changed
    descendantCache.clear();
  }
}
